import { useEffect, useState, useCallback } from 'react';
import { Plus, Users, Trash2, Edit2, AlertTriangle, X, Save, Search } from 'lucide-react';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type Operator = {
  id: string;
  tenantId: string;
  userId: string;
  pin: string;
  displayName: string;
  role: string;
  isActive: boolean;
  tenant?: { id: string; name: string };
  user?: { id: string; username: string; fullName: string; email: string | null };
};

type Tenant = { id: string; name: string };

const OPERATOR_ROLES: Array<{ value: string; label: string }> = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Menadžer' },
  { value: 'cashier', label: 'Kasir' },
  { value: 'waiter', label: 'Konobar' },
  { value: 'cook', label: 'Kuvar' },
  { value: 'warehouse', label: 'Magacioner' },
  { value: 'accountant', label: 'Knjigovođa' },
  { value: 'viewer', label: 'Pregled' },
];
const roleLabel = (r: string) => OPERATOR_ROLES.find((x) => x.value === r)?.label || r;

export function OperatorsPage() {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [editing, setEditing] = useState<Operator | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Operator | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const path = tenantFilter ? `/admin/operators?tenantId=${tenantFilter}` : '/admin/operators';
      const rows = await api.get<Operator[]>(path);
      setOperators(rows);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, [tenantFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    api.get<Tenant[]>('/admin/tenants').then((rows) => setTenants(rows.map((r) => ({ id: r.id, name: r.name })))).catch(() => {});
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.del(`/admin/operators/${deleteTarget.id}`);
    refresh();
  };

  const filtered = operators.filter((o) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (
      o.displayName.toLowerCase().includes(q) ||
      o.pin.includes(q) ||
      (o.user?.username || '').toLowerCase().includes(q) ||
      (o.tenant?.name || '').toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Operateri</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            POS kasiri za sve klijente - login PIN-om
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={btnPrimary}>
          <Plus size={14} /> Novi operater
        </button>
      </header>

      {error && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Pretraži po imenu, PIN-u, klijentu..."
            style={{ ...input, paddingLeft: 32 }}
          />
        </div>
        <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} style={{ ...input, maxWidth: 240 }}>
          <option value="">Svi klijenti</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Klijent</th>
                <th style={th}>Ime</th>
                <th style={th}>User</th>
                <th style={th}>PIN</th>
                <th style={th}>Rola</th>
                <th style={th}>Status</th>
                <th style={{ ...th, width: 100, textAlign: 'right' }}>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                  <Users size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <div>Nema operatera</div>
                </td></tr>
              )}
              {filtered.map((op) => (
                <tr key={op.id}>
                  <td style={{ ...td, color: 'var(--color-text-muted)' }}>{op.tenant?.name || '-'}</td>
                  <td style={{ ...td, color: 'var(--color-text-strong)', fontWeight: 600 }}>{op.displayName}</td>
                  <td style={{ ...td, color: 'var(--color-text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{op.user?.username || '-'}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 13, color: 'var(--color-text)' }}>{op.pin}</td>
                  <td style={{ ...td, color: 'var(--color-text-muted)' }}>{roleLabel(op.role)}</td>
                  <td style={td}>
                    {op.isActive
                      ? <span style={badgeActive}>Aktivan</span>
                      : <span style={badgeInactive}>Neaktivan</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => setEditing(op)} style={iconBtn} title="Izmeni"><Edit2 size={13} /></button>
                    <button onClick={() => setDeleteTarget(op)} style={{ ...iconBtn, color: '#fca5a5' }} title="Deaktiviraj"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <OperatorFormDialog
          operator={null}
          tenants={tenants}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); refresh(); }}
        />
      )}

      {editing && (
        <OperatorFormDialog
          operator={editing}
          tenants={tenants}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Deaktiviraj operatera"
          message={`Da li ste sigurni da želite da deaktivirate operatera "${deleteTarget.displayName}"? Više neće moći da se loguje na POS dok ga ponovo ne aktivirate.`}
          confirmLabel="Deaktiviraj"
          variant="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function OperatorFormDialog({ operator, tenants, onClose, onSaved }: {
  operator: Operator | null;
  tenants: Tenant[];
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const isEdit = !!operator;
  const [tenantId, setTenantId] = useState(operator?.tenantId || '');
  const [users, setUsers] = useState<Array<{ id: string; username: string; fullName: string }>>([]);
  const [userId, setUserId] = useState(operator?.userId || '');
  const [displayName, setDisplayName] = useState(operator?.displayName || '');
  const [pin, setPin] = useState(operator?.pin || '');
  const [role, setRole] = useState(operator?.role || 'admin');
  const [isActive, setIsActive] = useState(operator?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load users for selected tenant (only for create mode)
  useEffect(() => {
    if (isEdit || !tenantId) { setUsers([]); return; }
    api.get<Array<{ id: string; username: string; fullName: string }>>(`/admin/tenants/${tenantId}/users`)
      .then(setUsers)
      .catch(() => setUsers([]));
  }, [tenantId, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || !pin.trim()) { setError('Ime i PIN su obavezni'); return; }
    if (!/^\d{4}$/.test(pin)) { setError('PIN mora imati tačno 4 cifre'); return; }
    if (!isEdit && (!tenantId || !userId)) { setError('Klijent i user su obavezni'); return; }

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await api.put(`/admin/operators/${operator!.id}`, { displayName: displayName.trim(), pin: pin.trim(), role, isActive });
      } else {
        await api.post('/admin/operators', { tenantId, userId, displayName: displayName.trim(), pin: pin.trim(), role, isActive });
      }
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri čuvanju');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            {isEdit ? `Izmena operatera: ${operator!.displayName}` : 'Novi operater'}
          </h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>

        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!isEdit && (
            <>
              <Field label="Klijent (tenant) *">
                <select value={tenantId} onChange={(e) => { setTenantId(e.target.value); setUserId(''); }} required style={input}>
                  <option value="">- Izaberi klijenta -</option>
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </Field>
              <Field label="POS user (vlasnik operater naloga) *">
                <select value={userId} onChange={(e) => setUserId(e.target.value)} required disabled={!tenantId} style={input}>
                  <option value="">- Izaberi user-a -</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.fullName} ({u.username})</option>)}
                </select>
              </Field>
            </>
          )}

          <Field label="Prikazno ime *">
            <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoFocus={isEdit} required style={input} />
          </Field>

          <Field label="PIN (4 cifre) *">
            <input
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              required
              minLength={4}
              maxLength={4}
              style={{ ...input, fontFamily: 'monospace', fontSize: 14, letterSpacing: '0.2em' }}
              placeholder="1234"
            />
          </Field>

          <Field label="Rola (POS permisije) *">
            <select value={role} onChange={(e) => setRole(e.target.value)} required style={input}>
              {OPERATOR_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>

          {isEdit && (
            <Field label="">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
                <span style={{ fontSize: 13, color: 'var(--color-text)' }}>Aktivan (može se logovati na POS)</span>
              </label>
            </Field>
          )}

          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      {label && <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>}
      {children}
    </div>
  );
}

const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 480, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
const badgeActive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', fontWeight: 700 };
const badgeInactive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(100,116,139,0.2)', color: '#94a3b8', fontWeight: 700 };
